import { AlertTriangle, ArrowDown, ArrowUp, ChevronRight, Clock3, ExternalLink, GitBranch, Hourglass, Loader2, Pencil, RotateCcw, Target, TrendingUp } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { confirmModal, Modal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Field, FormSection } from '@/components/common/FormBits'
import { Kpi, Section } from '@/features/finance/components/FinanceBits'
import { toastError } from '@/features/finance/ui'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { fmtMoney } from '../shared/format'
import { useMoveStage, useResetStages, useSaveStage, useStages } from './api'
import { Dot } from './components/AdminBits'
import type { PipelineKind, PipelineStage } from './types'
import { TONE_BADGE } from './ui'

export function PipelinesPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const stages = useStages()
  const move = useMoveStage()
  const reset = useResetStages()
  const [pipeline, setPipeline] = useState<PipelineKind>('sales')
  const [editing, setEditing] = useState<PipelineStage | null>(null)
  const canWrite = can('pipelines.manage')

  const all = (stages.data ?? []).filter((s) => s.pipeline === pipeline)
  const open = all.filter((s) => s.outcome === 'open')
  const closing = all.filter((s) => s.outcome !== 'open')
  const openCount = open.reduce((n, s) => n + s.openCount, 0)
  const weighted = open.reduce((n, s) => n + (Number(s.openValue) * s.probability) / 100, 0)
  const cycle = open.reduce((n, s) => n + s.avgDays, 0)
  const breaches = open.filter((s) => s.slaDays != null && s.avgDays > s.slaDays)
  const isSales = pipeline === 'sales'

  const onReset = () =>
    void confirmModal({
      title: t('admin.pipelines.resetTitle'),
      description: t('admin.pipelines.resetBody', { pipeline: t(`admin.pipelines.kinds.${pipeline}`) }),
      confirmLabel: t('admin.pipelines.copyDefaults'),
    }).then((ok) => (ok ? reset.mutateAsync(pipeline).then(() => toast.success(t('common.saved'))).catch(toastError) : undefined))

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.admin')}
        title={t('admin.pipelines.title')}
        description={t('admin.pipelines.subtitle')}
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to={isSales ? '/customers/list?view=board' : '/customers/delivery'}>
                <ExternalLink />
                {isSales ? t('admin.pipelines.openBoard') : t('admin.pipelines.openDelivery')}
              </Link>
            </Button>
            {canWrite ? (
              <Button variant="outline" onClick={onReset} disabled={reset.isPending}>
                <RotateCcw />
                {t('admin.pipelines.copyDefaults')}
              </Button>
            ) : null}
          </>
        }
      />

      <Tabs value={pipeline} onValueChange={(v) => setPipeline(v as PipelineKind)} className="mb-4">
        <TabsList>
          <TabsTrigger value="sales">{t('admin.pipelines.kinds.sales')}</TabsTrigger>
          <TabsTrigger value="install">{t('admin.pipelines.kinds.install')}</TabsTrigger>
        </TabsList>
      </Tabs>

      {stages.isError ? (
        <ErrorState error={stages.error} onRetry={() => void stages.refetch()} />
      ) : !stages.data ? (
        <div className="space-y-4">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-64 rounded-xl" />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Kpi icon={GitBranch} tone="primary" label={t('admin.pipelines.kpi.open')} value={openCount} footer={t('admin.pipelines.kpi.openHint', { count: open.length })} />
            {isSales ? (
              <Kpi icon={TrendingUp} tone="success" label={t('admin.pipelines.kpi.weighted')} value={fmtMoney(weighted.toFixed(2))} footer={t('admin.pipelines.kpi.weightedHint')} />
            ) : (
              <Kpi icon={Target} tone="success" label={t('admin.pipelines.kpi.steps')} value={open.length} footer={t('admin.pipelines.kpi.stepsHint')} />
            )}
            <Kpi icon={Hourglass} tone="info" label={t('admin.pipelines.kpi.cycle')} value={t('admin.common.days', { count: cycle })} footer={t('admin.pipelines.kpi.cycleHint')} />
            <Kpi
              icon={AlertTriangle}
              tone={breaches.length ? 'danger' : 'neutral'}
              label={t('admin.pipelines.kpi.sla')}
              value={breaches.length}
              footer={breaches.length ? breaches.map((b) => b.name).join(', ') : t('admin.pipelines.kpi.slaOk')}
            />
          </div>

          <Section title={t('admin.pipelines.flowTitle')} icon={GitBranch} bodyClassName="p-3">
            <ol className="flex gap-2 overflow-x-auto pb-1">
              {open.map((s, i) => {
                const late = s.slaDays != null && s.avgDays > s.slaDays
                return (
                  <li key={s.id} className="flex min-w-48 flex-1 items-stretch gap-2">
                    <div className={cn('flex flex-1 flex-col gap-2 rounded-xl border p-3', late && 'border-destructive/40 bg-destructive/[0.04]')}>
                      <div className="flex items-center gap-2">
                        <Dot tone={s.tone} />
                        <span className="min-w-0 flex-1 truncate font-semibold">{s.name}</span>
                        <Badge tone={TONE_BADGE[s.tone]}>%{s.probability}</Badge>
                      </div>
                      <p className="text-2xl font-semibold tabular-nums">{s.openCount}</p>
                      {isSales ? <p className="text-xs tabular-nums text-muted-foreground">{fmtMoney(s.openValue)}</p> : null}
                      <div className="mt-auto flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock3 className="size-3" aria-hidden />
                        {t('admin.pipelines.avgVsSla', { avg: s.avgDays, sla: s.slaDays ?? '—' })}
                        {late ? <AlertTriangle className="ml-auto size-3.5 text-destructive" aria-label={t('admin.pipelines.slaBreach')} /> : null}
                      </div>
                    </div>
                    {i < open.length - 1 ? <ChevronRight className="size-4 shrink-0 self-center text-muted-foreground" aria-hidden /> : null}
                  </li>
                )
              })}
            </ol>
          </Section>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
            <Section title={t('admin.pipelines.stagesTitle')} bodyClassName="p-0">
              <ul className="divide-y">
                {open.map((s, i) => (
                  <li key={s.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold tabular-nums">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{s.name}</span>
                        <span className="font-mono text-xs text-muted-foreground">{s.code}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {s.requiredFields.length ? t('admin.pipelines.required', { fields: s.requiredFields.join(', ') }) : t('admin.pipelines.noRequired')}
                      </p>
                    </div>
                    <span className="text-xs tabular-nums text-muted-foreground">{s.slaDays != null ? t('admin.pipelines.slaDays', { count: s.slaDays }) : t('admin.pipelines.noSla')}</span>
                    {canWrite ? (
                      <div className="flex items-center gap-1">
                        <Button size="icon" variant="ghost" className="size-8" disabled={i === 0 || move.isPending} aria-label={t('admin.pipelines.moveUp', { name: s.name })} onClick={() => move.mutate({ id: s.id, dir: -1 }, { onError: toastError })}>
                          <ArrowUp />
                        </Button>
                        <Button size="icon" variant="ghost" className="size-8" disabled={i === open.length - 1 || move.isPending} aria-label={t('admin.pipelines.moveDown', { name: s.name })} onClick={() => move.mutate({ id: s.id, dir: 1 }, { onError: toastError })}>
                          <ArrowDown />
                        </Button>
                        <Button size="icon" variant="ghost" className="size-8" aria-label={t('admin.pipelines.editStage', { name: s.name })} onClick={() => setEditing(s)}>
                          <Pencil />
                        </Button>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </Section>
            <Section title={t('admin.pipelines.closingTitle')}>
              <p className="mb-3 text-xs text-muted-foreground">{t('admin.pipelines.closingHint')}</p>
              <ul className="space-y-2">
                {closing.map((s) => (
                  <li key={s.id} className="flex items-start gap-3 rounded-lg border p-3">
                    <Dot tone={s.tone} className="mt-1.5" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{s.name}</span>
                        <Badge tone={TONE_BADGE[s.tone]}>{t(`admin.pipelines.outcome.${s.outcome}`)}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{s.requiredFields.length ? t('admin.pipelines.required', { fields: s.requiredFields.join(', ') }) : t('admin.pipelines.noRequired')}</p>
                    </div>
                    {canWrite ? (
                      <Button size="icon" variant="ghost" className="size-8" aria-label={t('admin.pipelines.editStage', { name: s.name })} onClick={() => setEditing(s)}>
                        <Pencil />
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </Section>
          </div>
        </div>
      )}

      {editing ? <StageForm stage={editing} onDone={() => setEditing(null)} /> : null}
    </>
  )
}

function StageForm({ stage, onDone }: { stage: PipelineStage; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveStage()
  const [name, setName] = useState(stage.name)
  const [prob, setProb] = useState(String(stage.probability))
  const [sla, setSla] = useState(stage.slaDays == null ? '' : String(stage.slaDays))
  const [fields, setFields] = useState(stage.requiredFields.join(', '))
  const [error, setError] = useState('')
  const fixed = stage.outcome !== 'open'

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const p = Number(prob)
    const s = sla.trim() === '' ? null : Number(sla)
    if (!name.trim() || !Number.isInteger(p) || p < 0 || p > 100 || (s != null && (!Number.isInteger(s) || s < 1 || s > 365))) {
      setError(t('admin.pipelines.invalid'))
      return
    }
    save.mutate(
      { id: stage.id, input: { name: name.trim(), probability: p, slaDays: s, requiredFields: fields.split(',').map((f) => f.trim()).filter(Boolean) } },
      {
        onSuccess: () => {
          toast.success(t('common.saved'))
          onDone()
        },
        onError: toastError,
      },
    )
  }

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<GitBranch />}
      title={t('admin.pipelines.editTitle', { name: stage.name })}
      description={<span className="font-mono">{stage.code}</span>}
      onSubmit={submit}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <FormSection>
        <Field id="st-name" label={t('admin.pipelines.name')}>
          <Input id="st-name" value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>
        <Field id="st-prob" label={t('admin.pipelines.probability')} hint={fixed ? t('admin.pipelines.fixedHint') : undefined}>
          <Input id="st-prob" value={prob} onChange={(e) => setProb(e.target.value)} inputMode="numeric" disabled={fixed} />
        </Field>
        <Field id="st-sla" label={t('admin.pipelines.sla')} hint={t('admin.pipelines.slaHint')}>
          <Input id="st-sla" value={sla} onChange={(e) => setSla(e.target.value)} inputMode="numeric" disabled={fixed} />
        </Field>
        <Field id="st-fields" label={t('admin.pipelines.requiredFields')} hint={t('admin.pipelines.requiredHint')}>
          <Input id="st-fields" value={fields} onChange={(e) => setFields(e.target.value)} />
        </Field>
        {error ? <p className="text-xs text-destructive sm:col-span-2">{error}</p> : null}
      </FormSection>
    </Modal>
  )
}
